const API_URL = process.env.REACT_APP_API_URL || '';

export function getErrorMessage(payload, status) {
  if (typeof payload === 'string' && payload.trim()) {
    if (/<(?:!doctype|html|head|body|title|h1)\b/i.test(payload)) {
      if (status === 504) {
        return 'La operación tardó más de lo esperado. Puede seguir procesándose y recuperaremos su estado automáticamente.';
      }
      if ([502, 503].includes(status)) {
        return 'El servicio de producción no respondió a tiempo. Reintentaremos sin perder tu trabajo.';
      }
      return `El servicio devolvió una respuesta no válida (HTTP ${status}).`;
    }
    return payload;
  }

  if (Array.isArray(payload)) {
    const messages = payload
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const location = Array.isArray(item.loc) ? item.loc.join('.') : '';
          const message = item.msg || item.message || '';
          if (location && message) return `${location}: ${message}`;
          return message || location;
        }
        return '';
      })
      .filter(Boolean);

    if (messages.length) {
      return messages.join(' | ');
    }
  }

  if (payload && typeof payload === 'object') {
    if (typeof payload.detail === 'string') return payload.detail;
    if (Array.isArray(payload.detail)) return getErrorMessage(payload.detail, status);
    if (payload.detail && typeof payload.detail === 'object') {
      return getErrorMessage(payload.detail.message || payload.detail.msg || payload.detail.error, status);
    }
    if (typeof payload.message === 'string') return payload.message;
    if (typeof payload.error === 'string') return payload.error;
  }

  return `API Error: ${status}`;
}

// Key used to persist the user's active organization selection across page loads.
// Read directly from localStorage here (rather than via React context) so this
// plain module can attach the header without a circular dependency on
// AuthContext. Absent/empty => no header sent => 100% pre-existing behavior
// (backend falls back to the caller's first active membership).
const ACTIVE_ORG_STORAGE_KEY = 'noesis_active_org_id';

async function request(endpoint, options = {}) {
  const url = `${API_URL}${endpoint}`;
  const config = {
    headers: {
      ...options.headers,
    },
    ...options,
  };

  // Only set Content-Type to JSON if body is not FormData
  if (!(config.body instanceof FormData)) {
    config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
  }

  const token = localStorage.getItem('noesis_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // The literal "all" sentinel is only understood by a handful of explicitly
  // cross-org-aware superadmin endpoints (GET /api/admin/organizations, the
  // credits grant, GET /api/admin/usage/logs) — those call sites attach
  // `X-Organization-Id: all` themselves. Every other endpoint would 400 on
  // "all" (resolve_active_org_id only accepts it where allow_all=True), so
  // the generic auto-attach here only ever forwards a real org UUID and
  // otherwise behaves as if no org were selected (falls back to the
  // caller's default org) — matching "other pages treat 'all' as unselected".
  const activeOrgId = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
  if (activeOrgId && activeOrgId !== 'all' && !config.headers['X-Organization-Id']) {
    config.headers['X-Organization-Id'] = activeOrgId;
  }

  const response = await fetch(url, config);
  const rawText = await response.text();

  let payload = null;
  if (rawText) {
    try {
      payload = JSON.parse(rawText);
    } catch {
      payload = rawText;
    }
  }

  const loginAttempt = ['/api/auth/login', '/api/auth/portal-login'].includes(endpoint);
  if (response.status === 401 && token && !loginAttempt) {
    localStorage.removeItem('noesis_token');
    localStorage.removeItem('noesis_user');
    window.location.href = window.location.pathname.startsWith('/portal') ? '/portal/login' : '/login';
    const error = new Error('Tu sesión expiró. Inicia sesión nuevamente.');
    error.status = 401;
    error.payload = payload;
    throw error;
  }

  if (!response.ok) {
    const error = new Error(getErrorMessage(payload, response.status));
    error.status = response.status;
    error.payload = payload;
    error.code = payload?.code || payload?.detail?.code || payload?.detail?.error || payload?.error_code || null;
    error.retryAfter = response.headers.get('Retry-After');
    error.correlationId = response.headers.get('X-Correlation-Id') || response.headers.get('X-Request-Id');
    throw error;
  }

  if (response.status === 204) return null;

  return payload;
}

export const api = {
  // Agency endpoints resolve the agency independently from the selected client.
  agency: {
    settings: (organizationId) => request('/api/agency/settings', organizationId ? { headers: { 'X-Organization-Id': organizationId } } : {}),
    team: (organizationId) => request('/api/agency/team', organizationId ? { headers: { 'X-Organization-Id': organizationId } } : {}),
    overview: () => request('/api/agency/overview'),
    creditCatalog: () => request('/api/agency/credit-catalog'),
    updateCreditCatalog: (data) => request('/api/agency/credit-catalog', { method: 'PUT', body: JSON.stringify(data) }),
    creditAllocation: (id) => request(`/api/agency/clients/${id}/credit-allocation`),
    updateCreditAllocation: (id, data) => request(`/api/agency/clients/${id}/credit-allocation`, { method: 'PUT', body: JSON.stringify(data) }),
    clients: () => request('/api/agency/clients'),
    createClient: (data) => request('/api/agency/clients', { method: 'POST', body: JSON.stringify(data) }),
    client: (id) => request(`/api/agency/clients/${id}`),
    updateClient: (id, data) => request(`/api/agency/clients/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    grantCredits: (id, data) => request(`/api/agency/clients/${id}/credits`, { method: 'POST', body: JSON.stringify(data) }),
    usage: (id) => request(`/api/agency/clients/${id}/usage`),
    reservations: (id) => request(`/api/agency/clients/${id}/reservations`),
    reconcileReservation: (id, reservationId) => request(`/api/agency/clients/${id}/reservations/${reservationId}/reconcile`, { method: 'POST' }),
    analyzeMemory: (id, data) => request(`/api/agency/clients/${id}/memory/analyze`, { method: 'POST', body: JSON.stringify(data) }),
    memoryAnalysis: (id, operationId) => request(`/api/agency/clients/${id}/memory/analyses/${operationId}`),
    memoryHistory: (id, entryId) => request(`/api/agency/clients/${id}/memory/${entryId}/history`),
    users: (id) => request(`/api/agency/clients/${id}/users`),
    createUser: (id, data) => request(`/api/agency/clients/${id}/users`, { method: 'POST', body: JSON.stringify(data) }),
    updateUser: (id, userId, data) => request(`/api/agency/clients/${id}/users/${userId}`, { method: 'PATCH', body: JSON.stringify(data) }),
    memory: (id, params = {}) => request(`/api/agency/clients/${id}/memory?${new URLSearchParams(params)}`),
    saveMemory: (id, entryId, data) => request(`/api/agency/clients/${id}/memory${entryId ? `/${entryId}` : ''}`, { method: entryId ? 'PATCH' : 'POST', body: JSON.stringify(data) }),
    assets: (id, params = {}) => request(`/api/agency/clients/${id}/assets?${new URLSearchParams(params)}`),
    deliveries: (id, params = {}) => request(`/api/agency/clients/${id}/deliveries?${new URLSearchParams(params)}`),
    shareAsset: (id, assetId, shared) => request(`/api/agency/clients/${id}/assets/${assetId}/share`, { method: 'PUT', body: JSON.stringify({ shared }) }),
    approvals: (id) => request(`/api/agency/clients/${id}/approvals`),
    requestApproval: (id, data) => request(`/api/agency/clients/${id}/approvals`, { method: 'POST', body: JSON.stringify(data) }),
    boardReviews: (id, params = {}) => request(`/api/agency/clients/${id}/board-reviews?${new URLSearchParams(params)}`),
    requestBoardReview: (id, data) => request(`/api/agency/clients/${id}/board-reviews`, { method: 'POST', body: JSON.stringify(data) }),
    boards: (id) => request(`/api/agency/clients/${id}/boards`),
  },
  portal: {
    overview: () => request('/api/portal/overview'),
    deliveries: (params = {}) => request(`/api/portal/deliveries?${new URLSearchParams(params)}`),
    submissions: (params = {}) => request(`/api/portal/production/submissions?${new URLSearchParams(params)}`),
    submission: (id) => request(`/api/portal/production/submissions/${id}`),
    download: (submissionId, assetId) => request(`/api/portal/production/submissions/${submissionId}/assets/${assetId}/download`),
    campaigns: (params = {}) => request(`/api/portal/campaigns?${new URLSearchParams(params)}`),
    campaign: (id) => request(`/api/portal/campaigns/${id}`),
    createCampaign: (data) => request('/api/portal/campaigns', { method: 'POST', body: JSON.stringify(data) }),
    estimateCampaign: (data) => request('/api/portal/campaigns/estimate', { method: 'POST', body: JSON.stringify(data) }),
    uploadCampaignReference: (id, file, notes = '', options = {}) => {
      const data = new FormData(); data.append('file', file); data.append('notes', notes);
      if (options.uploadId) data.append('upload_id', options.uploadId);
      if (options.transcriptionText != null) data.append('transcription_text', options.transcriptionText);
      if (options.transcriptionOperationId) data.append('transcription_operation_id', options.transcriptionOperationId);
      return request(`/api/portal/campaigns/${id}/references`, { method: 'POST', body: data });
    },
    transcribeCampaignBrief: (file, { operationId, language = 'es' } = {}) => {
      const data = new FormData(); data.append('audio', file); data.append('operation_id', operationId); data.append('language', language);
      return request('/api/portal/campaigns/voice-brief/transcribe', { method: 'POST', body: data });
    },
    campaignVoiceOperation: (id) => request(`/api/portal/campaigns/voice-brief/operations/${id}`),
    boardReviews: (params = {}) => request(`/api/portal/board-reviews?${new URLSearchParams(params)}`),
    decideBoardReview: (id, data) => request(`/api/portal/board-reviews/${id}/decision`, { method: 'POST', body: JSON.stringify(data) }),
    decideSubmission: (id, data) => request(`/api/portal/production/submissions/${id}/decision`, { method: 'POST', body: JSON.stringify(data) }),
    feedback: (id, data) => request(`/api/portal/assets/${id}/feedback`, { method: 'POST', body: JSON.stringify(data) }),
    me: () => request('/api/portal/me'),
    assets: (params = {}) => request(`/api/portal/assets?${new URLSearchParams(params)}`),
    credits: () => request('/api/portal/credits'),
    approvals: (params = {}) => request(`/api/portal/approvals?${new URLSearchParams(params)}`),
    decide: (id, data) => request(`/api/portal/approvals/${id}/decision`, { method: 'POST', body: JSON.stringify(data) }),
  },
  production: {
    projects: (params = {}) => request(`/api/production/projects?${new URLSearchParams(params)}`),
    campaigns: (params = {}) => request(`/api/production/campaigns?${new URLSearchParams(params)}`),
    campaign: (id) => request(`/api/production/campaigns/${id}`),
    createCampaign: (data) => request('/api/production/campaigns', { method: 'POST', body: JSON.stringify(data) }),
    estimate: (data) => request('/api/production/campaigns/estimate', { method: 'POST', body: JSON.stringify(data) }),
    transcribeCampaignBrief: (file, { clientId, operationId, language = 'es' } = {}) => {
      const data = new FormData(); data.append('audio', file); data.append('client_id', clientId); data.append('operation_id', operationId); data.append('language', language);
      return request('/api/production/campaigns/voice-brief/transcribe', { method: 'POST', body: data });
    },
    campaignVoiceOperation: (id, clientId) => request(`/api/production/campaigns/voice-brief/operations/${id}?${new URLSearchParams({ client_id: clientId })}`),
    acceptCampaign: (id) => request(`/api/production/campaigns/${id}/accept`, { method: 'POST' }),
    inbox: () => request('/api/production/inbox'),
    obligations: () => request('/api/production/obligations'),
    project: (id) => request(`/api/production/projects/${id}`),
    updateProject: (id, data) => request(`/api/production/projects/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    updateReferenceTranscription: (scope, id, referenceId, data) => request(`/api/production/${scope}/${id}/references/${referenceId}`, { method: 'PATCH', body: JSON.stringify(data) }),
    uploadReference: (scope, id, file, notes = '', options = {}) => {
      const data = new FormData(); data.append('file', file); data.append('notes', notes);
      if (options.uploadId) data.append('upload_id', options.uploadId);
      if (options.transcriptionText != null) data.append('transcription_text', options.transcriptionText);
      if (options.transcriptionOperationId) data.append('transcription_operation_id', options.transcriptionOperationId);
      return request(`/api/production/${scope}/${id}/references`, { method: 'POST', body: data });
    },
    uploadExternalAsset: (id, file, notes = '', options = {}) => {
      const data = new FormData(); data.append('file', file); data.append('name', file.name); data.append('notes', notes);
      if (options.uploadId) data.append('upload_id', options.uploadId);
      if (options.nativeProjectId) data.append('native_project_id', options.nativeProjectId);
      return request(`/api/production/projects/${id}/external-assets`, { method: 'POST', body: data });
    },
    downloadAsset: (id, assetId) => request(`/api/production/projects/${id}/assets/${assetId}/download`),
    submitFinals: (id, data) => request(`/api/production/projects/${id}/submit-finals`, { method: 'POST', body: JSON.stringify(data) }),
  },
  // Auth
  login: (data) => request('/api/auth/login', { method: 'POST', body: JSON.stringify(data) }),
  portalLogin: (data) => request('/api/auth/portal-login', { method: 'POST', body: JSON.stringify(data) }),
  acceptInvite: (data) => request('/api/auth/accept-invite', { method: 'POST', body: JSON.stringify(data) }),
  getMe: () => request('/api/auth/me'),

  // Organizations (multi-org)
  getAllOrganizations: () => request('/api/admin/organizations', { headers: { 'X-Organization-Id': 'all' } }),
  grantOrganizationCredits: (orgId, data) =>
    request(`/api/admin/organizations/${orgId}/credits/grant`, { method: 'POST', body: JSON.stringify(data) }),

  // Credits & billing (user/org-facing)
  getCreditBalance: () => request('/api/credits/balance'),
  getCreditCatalog: () => request('/api/credits/catalog'),
  getCreditQuote: (params = {}) => request(`/api/credits/quote?${new URLSearchParams(params)}`),
  getCreditTransactions: (params = {}) => request(`/api/credits/transactions?${new URLSearchParams(params)}`),
  // Credits — admin views (crossOrg=true → X-Organization-Id: all, superadmin only)
  getAdminCreditsSummary: (crossOrg = false) =>
    request('/api/admin/credits/summary', crossOrg ? { headers: { 'X-Organization-Id': 'all' } } : {}),
  getAdminCreditTransactions: (params = {}, crossOrg = false) =>
    request(`/api/admin/credits/transactions?${new URLSearchParams(params)}`, crossOrg ? { headers: { 'X-Organization-Id': 'all' } } : {}),

  // Admin
  getUsers: () => request('/api/admin/users'),
  createUser: (data, organizationId) => request('/api/admin/users', { method: 'POST', body: JSON.stringify(data), ...(organizationId ? { headers: { 'X-Organization-Id': organizationId } } : {}) }),
  updateUser: (id, data, organizationId) => request(`/api/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(data), ...(organizationId ? { headers: { 'X-Organization-Id': organizationId } } : {}) }),
  deleteUser: (id) => request(`/api/admin/users/${id}`, { method: 'DELETE' }),
  getAuditLog: (params = {}) => request(`/api/admin/audit-log?${new URLSearchParams(params)}`),
  getUsageDashboard: (days = 30) => request(`/api/admin/usage/dashboard?days=${days}`),
  // `crossOrg=true` mirrors the `getUsageLogs` pattern below — explicitly opts
  // into `X-Organization-Id: all` (superadmin only, backend 403s otherwise).
  getAdminDashboard: (days = 30, crossOrg = false) =>
    request(`/api/admin/dashboard?days=${days}`, crossOrg ? { headers: { 'X-Organization-Id': 'all' } } : {}),
  // `crossOrg=true` explicitly opts into `X-Organization-Id: all` (superadmin
  // only, backend 403s otherwise) — callers must know they want this, since
  // the generic org-header auto-attach in `request()` intentionally never
  // forwards the "all" sentinel on its own.
  getUsageLogs: (params = {}, crossOrg = false) =>
    request(`/api/admin/usage/logs?${new URLSearchParams(params)}`, crossOrg ? { headers: { 'X-Organization-Id': 'all' } } : {}),
  // CSV export needs a Bearer token a plain <a href> can't send, so this does a
  // fetch+blob download instead: builds the same query string as getUsageLogs,
  // fetches the CSV as a blob, and triggers a browser download via a throwaway
  // <a>. Returns nothing — the download itself is the side effect.
  downloadUsageLogsCsv: async (params = {}, crossOrg = false) => {
    const token = localStorage.getItem('noesis_token');
    const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    if (crossOrg) {
      headers['X-Organization-Id'] = 'all';
    } else {
      const activeOrgId = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
      if (activeOrgId && activeOrgId !== 'all') headers['X-Organization-Id'] = activeOrgId;
    }
    const response = await fetch(`${API_URL}/api/admin/usage/logs/export.csv?${new URLSearchParams(params)}`, { headers });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(getErrorMessage(text, response.status));
    }
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `ai_usage_logs_${Date.now()}.csv`;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  // Admin - Invites
  createInvite: (data, organizationId) => request('/api/admin/invites', { method: 'POST', body: JSON.stringify(data), ...(organizationId ? { headers: { 'X-Organization-Id': organizationId } } : {}) }),
  getInvites: (organizationId) => request('/api/admin/invites', organizationId ? { headers: { 'X-Organization-Id': organizationId } } : {}),
  revokeInvite: (id, organizationId) => request(`/api/admin/invites/${id}`, { method: 'DELETE', ...(organizationId ? { headers: { 'X-Organization-Id': organizationId } } : {}) }),

  // Campaigns
  getCampaigns: (status) => request(`/api/campaigns${status ? `?status=${status}` : ''}`),
  getCampaign: (id) => request(`/api/campaigns/${id}`),
  updateCampaign: (id, data) => request(`/api/campaigns/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCampaign: (id) => request(`/api/campaigns/${id}`, { method: 'DELETE' }),

  // Campaign Assets (file upload)
  uploadCampaignAsset: (campaignId, file, fileType = 'reference') => {
    const formData = new FormData();
    formData.append('file', file);
    return request(`/api/campaigns/${campaignId}/assets?file_type=${fileType}`, {
      method: 'POST',
      body: formData,
    });
  },
  deleteCampaignAsset: (campaignId, assetId) =>
    request(`/api/campaigns/${campaignId}/assets/${assetId}`, { method: 'DELETE' }),

  // Campaign Creatives
  addCreative: (campaignId, data) =>
    request(`/api/campaigns/${campaignId}/creatives`, { method: 'POST', body: JSON.stringify(data) }),
  deleteCreative: (campaignId, creativeId) =>
    request(`/api/campaigns/${campaignId}/creatives/${creativeId}`, { method: 'DELETE' }),

  // Dashboard (real, org-scoped aggregation — see backend/app/api/dashboard.py)
  getDashboardOverview: () => request('/api/dashboard/overview'),

  // Notifications
  getNotifications: (params = {}, organizationId) => request(`/api/notifications?${new URLSearchParams(params)}`, organizationId ? { headers: { 'X-Organization-Id': organizationId } } : {}),
  getUnreadNotificationCount: (organizationId) => request('/api/notifications/unread-count', organizationId ? { headers: { 'X-Organization-Id': organizationId } } : {}),
  markNotificationRead: (id, organizationId) => request(`/api/notifications/${id}/read`, { method: 'POST', ...(organizationId ? { headers: { 'X-Organization-Id': organizationId } } : {}) }),
  markAllNotificationsRead: (organizationId) => request('/api/notifications/read-all', { method: 'POST', ...(organizationId ? { headers: { 'X-Organization-Id': organizationId } } : {}) }),

  // Client brand context
  getBrandKits: (params = {}) => request(`/api/brand-kits?${new URLSearchParams(params)}`),
  getBrandKit: (id) => request(`/api/brand-kits/${id}`),
  createBrandKit: (data) =>
    request('/api/brand-kits', { method: 'POST', body: JSON.stringify(data) }),
  updateBrandKit: (id, data) =>
    request(`/api/brand-kits/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteBrandKit: (id) =>
    request(`/api/brand-kits/${id}`, { method: 'DELETE' }),
  uploadBrandKitLogo: (brandKitId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return request(`/api/brand-kits/${brandKitId}/logo`, {
      method: 'POST',
      body: formData,
    });
  },

  // Studio - Templates
  getStudioModels: () => request('/api/studio/models'),
  getStudioTemplates: (category, mediaType, creativeMode) => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (mediaType) params.append('media_type', mediaType);
    if (creativeMode) params.append('creative_mode', creativeMode);
    const qs = params.toString();
    return request(`/api/studio/templates${qs ? `?${qs}` : ''}`);
  },
  // Rich template listing for the gallery (filters + community + sort)
  listStudioTemplates: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== false) q.append(k, v);
    });
    const qs = q.toString();
    return request(`/api/studio/templates${qs ? `?${qs}` : ''}`);
  },
  getStudioTemplate: (id) => request(`/api/studio/templates/${id}`),
  createStudioTemplate: (data) =>
    request('/api/studio/templates', { method: 'POST', body: JSON.stringify(data) }),
  updateStudioTemplate: (id, data) =>
    request(`/api/studio/templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteStudioTemplate: (id) =>
    request(`/api/studio/templates/${id}`, { method: 'DELETE' }),
  favoriteStudioTemplate: (id) =>
    request(`/api/studio/templates/${id}/favorite`, { method: 'POST' }),
  rateStudioTemplate: (id, stars) =>
    request(`/api/studio/templates/${id}/rate`, { method: 'POST', body: JSON.stringify({ stars }) }),
  publishStudioTemplate: (id, scope) =>
    request(`/api/studio/templates/${id}/publish`, { method: 'POST', body: JSON.stringify({ scope }) }),
  backfillTemplateReferences: (scope = 'missing') =>
    request(`/api/studio/templates/backfill-references?scope=${scope}`, { method: 'POST' }),
  uploadTemplateReference: (templateId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return request(`/api/studio/templates/${templateId}/reference`, {
      method: 'POST',
      body: formData,
    });
  },

  // Studio - AI Template Assistance
  generateTemplatePrompt: (data) =>
    request('/api/studio/templates/ai/generate-prompt', { method: 'POST', body: JSON.stringify(data) }),
  generateReferenceImage: (data) =>
    request('/api/studio/templates/ai/generate-reference', { method: 'POST', body: JSON.stringify(data) }),
  uploadStudioImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return request('/api/studio/uploads', { method: 'POST', body: formData });
  },

  // Studio - Generation
  generateStudioVariants: (data) =>
    request('/api/studio/generate', { method: 'POST', body: JSON.stringify(data) }),
  getStudioGenerations: (campaignId) =>
    request(`/api/studio/generations${campaignId ? `?campaign_id=${campaignId}` : ''}`),
  getStudioGeneration: (id) => request(`/api/studio/generations/${id}`),
  selectStudioVariant: (generationId, data) =>
    request(`/api/studio/generations/${generationId}/select`, { method: 'POST', body: JSON.stringify(data) }),

  // Studio - Editing
  sendEditInstruction: (data) =>
    request('/api/studio/edit', { method: 'POST', body: JSON.stringify(data) }),
  getEditSession: (sessionId) => request(`/api/studio/edit/${sessionId}`),

  // Studio - Video Generation
  getVideoModels: () => request('/api/studio/video/models'),
  generateVideo: (data) =>
    request('/api/studio/video/generate', { method: 'POST', body: JSON.stringify(data) }),
  getVideoGenerations: (campaignId) =>
    request(`/api/studio/video/generations${campaignId ? `?campaign_id=${campaignId}` : ''}`),
  getVideoGeneration: (id) => request(`/api/studio/video/generations/${id}`),
  selectVideoVariant: (generationId, data) =>
    request(`/api/studio/video/generations/${generationId}/select`, { method: 'POST', body: JSON.stringify(data) }),

  // Studio - Carousel Generation
  generateCarousel: (data) =>
    request('/api/studio/carousel/generate', { method: 'POST', body: JSON.stringify(data) }),
  getCarouselGenerations: (campaignId) =>
    request(`/api/studio/carousel/generations${campaignId ? `?campaign_id=${campaignId}` : ''}`),
  getCarouselGeneration: (id) => request(`/api/studio/carousel/generations/${id}`),
  regenerateCarouselSlide: (generationId, data) =>
    request(`/api/studio/carousel/generations/${generationId}/regenerate-slide`, { method: 'POST', body: JSON.stringify(data) }),
  updateCarouselSlide: (generationId, slideId, data) =>
    request(`/api/studio/carousel/generations/${generationId}/slides/${slideId}`, { method: 'PUT', body: JSON.stringify(data) }),
  uploadCanvasPreview: (generationId, slideId, data) =>
    request(`/api/studio/carousel/generations/${generationId}/slides/${slideId}/canvas-preview`, { method: 'POST', body: JSON.stringify(data) }),
  reorderCarouselSlides: (generationId, data) =>
    request(`/api/studio/carousel/generations/${generationId}/reorder`, { method: 'PUT', body: JSON.stringify(data) }),
  duplicateCarouselSlide: (generationId, data) =>
    request(`/api/studio/carousel/generations/${generationId}/duplicate-slide`, { method: 'POST', body: JSON.stringify(data) }),
  insertCarouselSlide: (generationId, data) =>
    request(`/api/studio/carousel/generations/${generationId}/insert-slide`, { method: 'POST', body: JSON.stringify(data) }),
  exportCarousel: async (generationId, format = 'zip') => {
    if (format === 'zip') {
      const token = localStorage.getItem('noesis_token');
      const res = await fetch(`${API_URL}/api/studio/carousel/generations/${generationId}/export?format=zip`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al exportar carrusel');
      return res.blob();
    }
    return request(`/api/studio/carousel/generations/${generationId}/export?format=${format}`);
  },
  deleteCarousel: (generationId) =>
    request(`/api/studio/carousel/generations/${generationId}`, { method: 'DELETE' }),

  // Creative Assets (unified asset library)
  getCreativeAssets: (params = {}) =>
    request(`/api/creative-assets?${new URLSearchParams(params)}`),
  getCreativeAssetsGrouped: (params = {}) =>
    request(`/api/creative-assets/grouped?${new URLSearchParams(params)}`),
  getCreativeAssetStats: () => request('/api/creative-assets/stats'),
  getCreativeAsset: (id) => request(`/api/creative-assets/${id}`),
  toggleCreativeAssetFavorite: (id, isFavorite) =>
    request(`/api/creative-assets/${id}/favorite`, {
      method: 'PATCH',
      body: JSON.stringify({ is_favorite: isFavorite }),
    }),
  deleteCreativeAsset: (id) =>
    request(`/api/creative-assets/${id}`, { method: 'DELETE' }),

  // Clair Labs 2.0 (AI Commercial Video Studio)
  clairLabs: {
    // config
    getConfig: (videoModel) =>
      request(`/api/clair-labs/config${videoModel ? `?video_model=${encodeURIComponent(videoModel)}` : ''}`),
    getModels: () => request('/api/clair-labs/models'),
    getDurationPresets: (videoModel) =>
      request(`/api/clair-labs/duration-presets${videoModel ? `?video_model=${encodeURIComponent(videoModel)}` : ''}`),
    // influencers
    getInfluencers: (niche) =>
      request(`/api/clair-labs/influencers${niche ? `?niche=${niche}` : ''}`),
    importInfluencers: () =>
      request('/api/clair-labs/influencers/import-system', { method: 'POST' }),
    // products
    createProduct: (data) =>
      request('/api/clair-labs/products', { method: 'POST', body: JSON.stringify(data) }),
    getProducts: () => request('/api/clair-labs/products'),
    getProduct: (id) => request(`/api/clair-labs/products/${id}`),
    updateProduct: (id, data) =>
      request(`/api/clair-labs/products/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    uploadProductImage: (productId, file, role = 'product_hero') => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('role', role);
      return request(`/api/clair-labs/products/${productId}/images`, { method: 'POST', body: formData });
    },
    deleteProductImage: (productId, refId) =>
      request(`/api/clair-labs/products/${productId}/images/${refId}`, { method: 'DELETE' }),
    // projects
    createProject: (data) =>
      request('/api/clair-labs/projects', { method: 'POST', body: JSON.stringify(data) }),
    getProjects: (params = {}) =>
      request(`/api/clair-labs/projects?${new URLSearchParams(params)}`),
    getProject: (id) => request(`/api/clair-labs/projects/${id}`),
    updateProject: (id, data) =>
      request(`/api/clair-labs/projects/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteProject: (id) =>
      request(`/api/clair-labs/projects/${id}`, { method: 'DELETE' }),
    // references
    uploadReference: (projectId, file, role = 'product_hero', isPrimary = false) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('role', role);
      formData.append('is_primary', isPrimary);
      return request(`/api/clair-labs/projects/${projectId}/references`, { method: 'POST', body: formData });
    },
    getReferences: (projectId) => request(`/api/clair-labs/projects/${projectId}/references`),
    deleteReference: (projectId, refId) =>
      request(`/api/clair-labs/projects/${projectId}/references/${refId}`, { method: 'DELETE' }),
    // planning
    generatePlan: (projectId, data = {}) =>
      request(`/api/clair-labs/projects/${projectId}/generate-plan`, { method: 'POST', body: JSON.stringify(data) }),
    regeneratePlan: (projectId, data = {}) =>
      request(`/api/clair-labs/projects/${projectId}/regenerate-plan`, { method: 'POST', body: JSON.stringify(data) }),
    updatePlan: (projectId, data) =>
      request(`/api/clair-labs/projects/${projectId}/plan`, { method: 'PATCH', body: JSON.stringify(data) }),
    approvePlan: (projectId) =>
      request(`/api/clair-labs/projects/${projectId}/approve-plan`, { method: 'POST' }),
    // storyboard
    generateScenePrompts: (projectId) =>
      request(`/api/clair-labs/projects/${projectId}/generate-scenes`, { method: 'POST' }),
    generateStoryboardPoster: (projectId, data = {}) =>
      request(`/api/clair-labs/projects/${projectId}/generate-storyboard-poster`, { method: 'POST', body: JSON.stringify(data) }),
    updateScene: (projectId, sceneId, data) =>
      request(`/api/clair-labs/projects/${projectId}/scenes/${sceneId}`, { method: 'PATCH', body: JSON.stringify(data) }),
    reorderScenes: (projectId, data) =>
      request(`/api/clair-labs/projects/${projectId}/scenes/reorder`, { method: 'POST', body: JSON.stringify(data) }),
    lockScene: (projectId, sceneId, isLocked) =>
      request(`/api/clair-labs/projects/${projectId}/scenes/${sceneId}/lock`, { method: 'POST', body: JSON.stringify({ is_locked: isLocked }) }),
    regenerateKeyframes: (projectId, sceneId) =>
      request(`/api/clair-labs/projects/${projectId}/scenes/${sceneId}/regenerate-keyframes`, { method: 'POST' }),
    regenerateScene: (projectId, sceneId, data = {}) =>
      request(`/api/clair-labs/projects/${projectId}/scenes/${sceneId}/regenerate`, { method: 'POST', body: JSON.stringify(data) }),
    // production
    generateKeyframes: (projectId, data = {}) =>
      request(`/api/clair-labs/projects/${projectId}/generate-keyframes`, { method: 'POST', body: JSON.stringify(data) }),
    generateVideos: (projectId, data = {}) =>
      request(`/api/clair-labs/projects/${projectId}/generate-videos`, { method: 'POST', body: JSON.stringify(data) }),
    generateKeyframe: (projectId, sceneId) =>
      request(`/api/clair-labs/projects/${projectId}/scenes/${sceneId}/generate-keyframe`, { method: 'POST' }),
    generateVideo: (projectId, sceneId) =>
      request(`/api/clair-labs/projects/${projectId}/scenes/${sceneId}/generate-video`, { method: 'POST' }),
    getProductionStatus: (projectId) =>
      request(`/api/clair-labs/projects/${projectId}/production-status`),
    // assembly
    assembleProject: (projectId, data) =>
      request(`/api/clair-labs/projects/${projectId}/assemble`, { method: 'POST', body: JSON.stringify(data) }),
    getRenders: (projectId) => request(`/api/clair-labs/projects/${projectId}/renders`),
    selectRender: (projectId, renderId) =>
      request(`/api/clair-labs/projects/${projectId}/renders/${renderId}/select-final`, { method: 'POST' }),
    getDownload: (projectId) => request(`/api/clair-labs/projects/${projectId}/download`),
    getHistory: (projectId) => request(`/api/clair-labs/projects/${projectId}/history`),
  },

  // Clair Labs 3.0. These contracts are intentionally additive: V2 keeps its
  // exact endpoints above for existing projects while the route dispatcher
  // selects this provider-neutral, task-based workspace for pipeline_version=3.
  clairLabsV3: {
    getConfig: () => request('/api/clair-labs/config'),
    createProject: (data) =>
      request('/api/clair-labs/projects', {
        method: 'POST',
        body: JSON.stringify({ ...data, pipeline_version: 3 }),
      }),
    getProjects: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return request(`/api/clair-labs/projects${query ? `?${query}` : ''}`);
    },
    getProject: (projectId) => request(`/api/clair-labs/projects/${projectId}`),
    updateProject: (projectId, data) =>
      request(`/api/clair-labs/projects/${projectId}`, { method: 'PATCH', body: JSON.stringify(data) }),
    getProjectCharacters: (projectId) => request(`/api/clair-labs/projects/${projectId}/characters`),
    selectPrimaryCharacter: (projectId, characterId) =>
      request(`/api/clair-labs/projects/${projectId}/characters/primary`, {
        method: 'PUT',
        body: JSON.stringify({ character_id: characterId, role: 'primary', is_identity_reference: true }),
      }),
    removePrimaryCharacter: (projectId) =>
      request(`/api/clair-labs/projects/${projectId}/characters/primary`, { method: 'DELETE' }),

    transcribeVoiceBrief: (projectId, file, options = {}) => {
      const formData = new FormData();
      formData.append('audio', file);
      if (options.persistAudio != null) formData.append('persist_audio', String(options.persistAudio));
      return request(`/api/clair-labs/projects/${projectId}/voice-brief/transcribe`, {
        method: 'POST',
        body: formData,
      });
    },
    uploadVoiceBrief: (projectId, file, options = {}) => {
      const formData = new FormData();
      formData.append('audio', file);
      if (options.persistAudio != null) formData.append('persist_audio', String(options.persistAudio));
      return request(`/api/clair-labs/projects/${projectId}/voice-brief`, { method: 'POST', body: formData });
    },
    deleteVoiceBrief: (projectId) =>
      request(`/api/clair-labs/projects/${projectId}/voice-brief`, { method: 'DELETE' }),
    updateCreativeIntent: (projectId, data) => {
      const { text_brief: textBrief, ...payload } = data;
      return request(`/api/clair-labs/projects/${projectId}/creative-intent`, {
        method: 'PATCH',
        body: JSON.stringify({ ...payload, ...(textBrief !== undefined ? { free_text_brief: textBrief } : {}) }),
      });
    },

    uploadReference: (projectId, file, data = {}) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('role', data.role || (file.type.startsWith('video/') ? 'movement_performance' : 'product_main'));
      formData.append('is_primary', String(Boolean(data.isPrimary)));
      formData.append('use_for_generation', String(Boolean(data.useForGeneration)));
      formData.append('rights_confirmed', String(Boolean(data.rightsConfirmed)));
      if (data.sourceProvenance) formData.append('source_provenance', data.sourceProvenance);
      if (data.keepInstruction) formData.append('keep_instruction', data.keepInstruction);
      if (data.doNotCopy) formData.append('do_not_copy', data.doNotCopy);
      return request(`/api/clair-labs/projects/${projectId}/references`, { method: 'POST', body: formData });
    },
    getReferences: (projectId) => request(`/api/clair-labs/projects/${projectId}/references`),
    updateReference: (projectId, referenceId, data) =>
      request(`/api/clair-labs/projects/${projectId}/references/${referenceId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    bindAuthorizedPersonAsset: (projectId, referenceId, data) =>
      request(`/api/clair-labs/projects/${projectId}/references/${referenceId}/provider-authorized-asset`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    reorderReferences: (projectId, referenceIds) =>
      request(`/api/clair-labs/projects/${projectId}/references/reorder`, {
        method: 'POST',
        body: JSON.stringify({ reference_ids: referenceIds }),
      }),
    deleteReference: (projectId, referenceId) =>
      request(`/api/clair-labs/projects/${projectId}/references/${referenceId}`, { method: 'DELETE' }),
    analyzeReference: (projectId, referenceId) =>
      request(`/api/clair-labs/projects/${projectId}/references/${referenceId}/analyze`, { method: 'POST' }),
    getReferenceAnalysis: (projectId, referenceId) =>
      request(`/api/clair-labs/projects/${projectId}/references/${referenceId}/analysis`),

    generateDirectorBoard: (projectId, data = {}) => {
      const { idempotencyKey, quality_profile: qualityProfile, ...payload } = data;
      return request(`/api/clair-labs/projects/${projectId}/director-board/generate`, {
        method: 'POST',
        body: JSON.stringify({ ...payload, quality_profile: qualityProfile, idempotency_key: idempotencyKey }),
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
      });
    },
    getDirectorBoard: (projectId) =>
      request(`/api/clair-labs/projects/${projectId}/director-board`),
    uploadDirectorBoardRevisionContext: (projectId, file) => {
      const formData = new FormData();
      formData.append('image', file);
      return request(`/api/clair-labs/projects/${projectId}/director-board/revision-context`, {
        method: 'POST', body: formData,
      });
    },
    previewDirectorBoardRevision: (projectId, data) =>
      request(`/api/clair-labs/projects/${projectId}/director-board/revise`, {
        method: 'POST',
        body: JSON.stringify({ ...data, confirm_cost: false, preview_only: true }),
      }),
    reviseDirectorBoard: (projectId, data, idempotencyKey) =>
      request(`/api/clair-labs/projects/${projectId}/director-board/revise`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
        body: JSON.stringify({ ...data, confirm_cost: true, preview_only: false }),
      }),
    approveDirectorBoard: (projectId, versionId) =>
      request(`/api/clair-labs/projects/${projectId}/director-board/${versionId}/approve`, { method: 'POST' }),
    restoreDirectorBoard: (projectId, versionId) =>
      request(`/api/clair-labs/projects/${projectId}/director-board/${versionId}/restore`, { method: 'POST' }),

    getGenerationQuote: (projectId, params = {}) => {
      const query = new URLSearchParams(
        Object.entries(params).reduce((acc, [key, value]) => {
          if (value != null && value !== '') acc[key] = String(value);
          return acc;
        }, {})
      ).toString();
      return request(`/api/clair-labs/projects/${projectId}/generation-quote${query ? `?${query}` : ''}`);
    },
    generateVideo: (projectId, data, idempotencyKey) =>
      request(`/api/clair-labs/projects/${projectId}/generate-video`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
        body: JSON.stringify({ ...data, idempotency_key: data.idempotency_key || idempotencyKey }),
      }),
    getGenerationStatus: (projectId) =>
      request(`/api/clair-labs/projects/${projectId}/generation-status`),
    uploadOutro: (projectId, file) => {
      const formData = new FormData();
      formData.append('video', file);
      return request(`/api/clair-labs/projects/${projectId}/outro`, { method: 'POST', body: formData });
    },
    deleteOutro: (projectId) =>
      request(`/api/clair-labs/projects/${projectId}/outro`, { method: 'DELETE' }),
    finalize: (projectId, data, idempotencyKey) =>
      request(`/api/clair-labs/projects/${projectId}/finalize`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
        body: JSON.stringify({ ...data, idempotency_key: data.idempotency_key || idempotencyKey }),
      }),
    getDownload: (projectId) => request(`/api/clair-labs/projects/${projectId}/download`),
    publishToAssetLibrary: (projectId) =>
      request(`/api/clair-labs/projects/${projectId}/publish-to-asset-library`, { method: 'POST' }),
  },

  aiCharacters: {
    list: (params = {}) => {
      const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value != null && value !== '')).toString();
      return request(`/api/ai-characters${query ? `?${query}` : ''}`);
    },
    get: (id) => request(`/api/ai-characters/${id}`),
    create: (data) => request('/api/ai-characters', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => request(`/api/ai-characters/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    remove: (id) => request(`/api/ai-characters/${id}`, { method: 'DELETE' }),
    uploadAsset: (id, file, options = {}) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('asset_role', options.assetRole || 'master_portrait');
      formData.append('rights_attested', String(Boolean(options.rightsAttested)));
      return request(`/api/ai-characters/${id}/assets`, { method: 'POST', body: formData });
    },
    deleteAsset: (id, assetId) => request(`/api/ai-characters/${id}/assets/${assetId}`, { method: 'DELETE' }),
    generate: (id, data = {}) => request(`/api/ai-characters/${id}/generate`, { method: 'POST', body: JSON.stringify(data) }),
    syncBytePlus: (id) => request(`/api/ai-characters/${id}/sync-byteplus`, { method: 'POST' }),
    providerStatus: (id) => request(`/api/ai-characters/${id}/provider-status`),
    startRealPersonVerification: (id, data) => request(`/api/ai-characters/${id}/real-person-verification`, { method: 'POST', body: JSON.stringify(data) }),
    realPersonVerificationStatus: (id, verificationId = null) => {
      const query = verificationId ? `?verification_id=${encodeURIComponent(verificationId)}` : '';
      return request(`/api/ai-characters/${id}/real-person-verification/status${query}`);
    },
  },

};

export default api;
